import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { simulate } from '../mock-utils'
import { MOCK_PAID_FIELD, pagosMock } from '../pagos/mock'
import type { PaymentKind } from '../pagos/types'
import { money } from '../types'
import { BANK_DELAY_DAYS } from './real'
import type { Deposit, FianzaService } from './types'

/** Fianza de 300 € en preautorización del Ático Plaza Mayor (Pagos.dc.html, PantallaFianza.dc.html), según la fase. */
const BASE: Deposit = {
  amount: money(30000),
  kind: 'preauth',
  status: 'pending',
  cardLast4: '4421',
  heldAt: null,
  holdUntil: '2026-10-06T11:00:00+02:00',
  reviewDeadline: null,
  releasedAt: null,
  releasedAmount: null,
  bankDelayDays: BANK_DELAY_DAYS,
  availableWindow: null,
  timeline: [],
  deduction: null,
  claim: null,
}

export const MOCK_DEPOSIT_PENDING: Deposit = {
  ...BASE,
  timeline: [
    { code: 'held', state: 'current', date: null, expected: null },
    { code: 'stay_ended', state: 'future', date: null, expected: { from: '2026-10-06T11:00:00+02:00' } },
    { code: 'review', state: 'future', date: null, expected: null },
    { code: 'released', state: 'future', date: null, expected: { from: '2026-10-08' } },
  ],
}

export const MOCK_DEPOSIT_HELD: Deposit = {
  ...BASE,
  status: 'held',
  heldAt: '2026-09-28T10:12:00+02:00',
  timeline: [
    { code: 'held', state: 'done', date: '2026-09-28T10:12:00+02:00', expected: null },
    { code: 'stay_ended', state: 'current', date: null, expected: { from: '2026-10-06T11:00:00+02:00' } },
    { code: 'review', state: 'future', date: null, expected: { from: '2026-10-06', to: '2026-10-08' } },
    { code: 'released', state: 'future', date: null, expected: { from: '2026-10-08' } },
  ],
}

export const MOCK_DEPOSIT_REVIEW: Deposit = {
  ...MOCK_DEPOSIT_HELD,
  status: 'review',
  reviewDeadline: '2026-10-08T11:00:00+02:00',
  timeline: [
    { code: 'held', state: 'done', date: '2026-09-28T10:12:00+02:00', expected: null },
    { code: 'stay_ended', state: 'done', date: '2026-10-06T11:00:00+02:00', expected: null },
    { code: 'review', state: 'current', date: null, expected: { from: '2026-10-06', to: '2026-10-08' } },
    { code: 'released', state: 'future', date: null, expected: { from: '2026-10-08' } },
  ],
}

export const MOCK_DEPOSIT_RELEASED: Deposit = {
  ...MOCK_DEPOSIT_HELD,
  status: 'released',
  releasedAt: '2026-10-07T09:30:00+02:00',
  releasedAmount: money(30000),
  availableWindow: { from: '2026-10-10', to: '2026-10-17' },
  timeline: [
    { code: 'held', state: 'done', date: '2026-09-28T10:12:00+02:00', expected: null },
    { code: 'stay_ended', state: 'done', date: '2026-10-06T11:00:00+02:00', expected: null },
    { code: 'review', state: 'done', date: '2026-10-07T09:00:00+02:00', expected: null },
    { code: 'released', state: 'done', date: '2026-10-07T09:30:00+02:00', expected: null },
    { code: 'available', state: 'current', date: null, expected: { from: '2026-10-10', to: '2026-10-17' } },
  ],
}

/** Pagos.dc.html `deduction`: 60 € por la lámpara del salón, 240 € liberados, 14 días para reclamar. */
export const MOCK_DEPOSIT_DEDUCTION: Deposit = {
  ...MOCK_DEPOSIT_HELD,
  status: 'deducted',
  releasedAt: '2026-10-07T12:15:00+02:00',
  releasedAmount: money(24000),
  availableWindow: { from: '2026-10-10', to: '2026-10-17' },
  deduction: {
    reason: 'lámpara del salón rota',
    photos: ['', ''],
    breakdown: [{ concept: 'Lámpara de pie (reposición)', amount: money(-6000) }],
    deducted: money(6000),
    released: money(24000),
    claimDeadline: '2026-10-21T12:15:00+02:00',
  },
  timeline: [
    { code: 'held', state: 'done', date: '2026-10-03T15:58:00+02:00', expected: null },
    { code: 'stay_ended', state: 'done', date: '2026-10-06T10:40:00+02:00', expected: null },
    { code: 'review', state: 'done', date: '2026-10-07T11:00:00+02:00', expected: null },
    { code: 'deducted', state: 'done', date: '2026-10-07T12:15:00+02:00', expected: null },
    { code: 'available', state: 'current', date: null, expected: { from: '2026-10-10', to: '2026-10-17' } },
  ],
}

const CLAIM_FIELD = 'depositClaim'

/**
 * Códigos de reserva (además de las fases, `hotel` sin fianza y error/offline/lento-fianza):
 * `fianza-pendiente`, `fianza-retenida`, `revision`, `fianza-liberada`, `fianza-deduccion` y `reclamacion-falla`.
 * Tipo (D7): preautorización por defecto; `fianza-cargo` → cargo; `fianza-sin-tipo` → sin tipo (copy neutro, como hoy en real).
 */
function depositFor(stay: Parameters<FianzaService['getDeposit']>[0]): Deposit {
  const g = stay.guestCode
  if (g.includes('fianza-pendiente')) return MOCK_DEPOSIT_PENDING
  if (g.includes('fianza-retenida')) return MOCK_DEPOSIT_HELD
  if (g.includes('fianza-liberada')) return MOCK_DEPOSIT_RELEASED
  if (g.includes('fianza-deduccion')) return MOCK_DEPOSIT_DEDUCTION
  switch (stay.phase) {
    case 'before':
      return MOCK_DEPOSIT_PENDING
    case 'arrival':
    case 'during':
      return MOCK_DEPOSIT_HELD
    case 'checkout':
      return stay.guestCode.includes('revision') ? MOCK_DEPOSIT_REVIEW : MOCK_DEPOSIT_HELD
    case 'after':
      return stay.guestCode.includes('revision') ? MOCK_DEPOSIT_REVIEW : MOCK_DEPOSIT_RELEASED
    default:
      return MOCK_DEPOSIT_PENDING
  }
}

export const fianzaMock: FianzaService = {
  async getDeposit(stay, _lang, signal) {
    await simulate(stay.guestCode, 'fianza', signal)
    if (stay.guestCode.includes('hotel')) return null
    let deposit = depositFor(stay)
    // Tras reservarla en el mock, deja de estar pendiente.
    if (deposit.status === 'pending' && (readLocal<PaymentKind[]>(stay.guestCode, MOCK_PAID_FIELD) ?? []).includes('deposit')) {
      const now = new Date().toISOString()
      deposit = { ...MOCK_DEPOSIT_HELD, heldAt: now, timeline: MOCK_DEPOSIT_HELD.timeline.map((it) => (it.code === 'held' ? { ...it, date: now } : it)) }
    }
    const kind = stay.guestCode.includes('fianza-cargo') ? 'charge' : stay.guestCode.includes('fianza-sin-tipo') ? 'unknown' : deposit.kind
    return { ...deposit, kind, claim: readLocal<{ submittedAt: string }>(stay.guestCode, CLAIM_FIELD) }
  },
  async reserveDeposit(stay, amount, card, lang, signal) {
    await simulate(stay.guestCode, 'fianza', signal)
    // Con la fianza simulada, el cobro también lo es (mismo banco simulado que pagos).
    return pagosMock.pay(stay, { kind: 'deposit', amount, card }, lang, signal)
  },
  async claim(stay) {
    await simulate(stay.guestCode, 'fianza')
    if (stay.guestCode.includes('reclamacion-falla')) throw new ServiceError('server', 'mock claim error')
    const result = { submittedAt: new Date().toISOString() }
    writeLocal(stay.guestCode, CLAIM_FIELD, result)
    return result
  },
}
